import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import { useParams } from "@solidjs/router";

import FuturePayment, {
  FuturePaymentCanada,
  FuturePaymentUsa,
} from "@repo/kikoff-models/FuturePayment";
import CreditLine, {
  CreditLineCanada,
  CreditLineUsa,
} from "@repo/kikoff-models/lending/CreditLine";
import PaymentMethod from "@repo/kikoff-models/payments/PaymentMethod";
import Recurrence, {
  RecurrenceCanada,
  RecurrenceUsa,
} from "@repo/kikoff-models/Recurrence";
import Transaction, {
  TransactionCanada,
  TransactionUsa,
} from "@repo/kikoff-models/Transaction";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { User } from "@repo/protobuf/gen/web/public/user_pb";
import Protobuf from "@repo/protobuf/utils";
import Telemetry from "@repo/telemetry";
import assert from "@repo/utils/assert";
import Chainable from "@repo/utils/Chainable";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import UFunction from "@repo/utils/UFunction";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import analytics, { track } from "~/analytics/client";

import { useSubscriptions } from "./subscriptions";
import { useTransactions } from "./transactions";

const initialState = {
  byId: {} as Record<CreditLine.Id, CreditLine>,
  loaded: false,
  activeCa: null as null | CreditLine.Id,
  statementsByCreditLineId: {} as Record<CreditLine.Id, CreditLine.Statement[]>,
  transactionsByCreditLineId: {} as Record<
    CreditLine.Id,
    Transaction.Id[] & { partial?: boolean }
  >,
  predictedPaymentsByCreditLineId: {} as Record<CreditLine.Id, FuturePayment[]>,
  expectedDueDate: null as Date | null,
};

export const { Provider, useCreditLines, Context } = FeatureStore.init(
  "CreditLines",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [subscriptions, setSubscriptions] = useSubscriptions();
    const [transactions, setTransactions] = useTransactions();

    const params = useParams<{ creditLineId?: CreditLine.Id }>();

    const accessors = {
      loaded: attachAction(
        () => store.loaded,
        () => actions.fetch()
      ),
      byId: attachAction(
        (id: CreditLine.Id) => store.byId[id],
        (id) => actions.fetch(id)
      ),
      activeCa: attachAction(
        () => store.byId[store.activeCa!],
        () => actions.fetch(),
        () => () => store.loaded
      ),
      current: attachAction(
        () => params.creditLineId && accessors.byId(params.creditLineId),
        () => actions.fetch(params.creditLineId)
      ),
      statementsFor: attachAction(
        (id: CreditLine.Id | undefined) =>
          (id && store.statementsByCreditLineId[id]) || [],
        (id) => id && actions.fetchStatements(id),
        (id) => () => id && store.statementsByCreditLineId[id]
      ),
      transactionsFor: attachAction(
        (id: CreditLine.Id | undefined) =>
          (id &&
            store.transactionsByCreditLineId[id]?.map(
              (id) => transactions.byId(id)!
            )) ||
          [],
        (id) => id && actions.fetchTransactions(id),
        (id) => () =>
          id &&
          store.transactionsByCreditLineId[id] &&
          !store.transactionsByCreditLineId[id]!.partial
      ),
      predictedPaymentsFor: attachAction(
        (id: CreditLine.Id | undefined) =>
          (id && store.predictedPaymentsByCreditLineId[id]) || [],
        (id) => id && actions.fetchPredictedPayments(id),
        (id) => () => id && store.predictedPaymentsByCreditLineId[id]
      ),
      // Expected due date of credit line before it's opened
      expectedDueDate: attachAction(
        () => store.expectedDueDate,
        () => actions.fetchExpectedDueDate(),
        () => (res) => res
      ),
    };

    const mutations = {
      one(creditLine: CreditLine) {
        if (creditLine.closedAt) {
          if (store.activeCa === creditLine.id) setStore("activeCa", null);
        } else {
          if (store.activeCa && store.activeCa !== creditLine.id)
            Telemetry.captureError(
              new Error("Unexpectedly encountered multiple open credit lines.")
            );
          setStore("activeCa", creditLine.id);
        }

        setStore("byId", creditLine.id, reconcile(creditLine));
      },
      many(creditLines: CreditLine[]) {
        batch(() => {
          for (const creditLine of creditLines) {
            mutations.one(creditLine);
          }
        });
      },
      all(creditLines: CreditLine[]) {
        batch(() => {
          mutations.many(creditLines);
          setStore("loaded", true);
        });
      },
      transactionsFor(
        creditLineId: CreditLine.Id,
        transactions: Transaction[]
      ) {
        batch(() => {
          setTransactions.many(transactions);
          setStore(
            "transactionsByCreditLineId",
            creditLineId,
            transactions.map(({ id }) => id)
          );
        });
      },
      addTransactionFor(creditLineId: CreditLine.Id, transaction: Transaction) {
        batch(() => {
          setTransactions.one(transaction);
          setStore("transactionsByCreditLineId", creditLineId, (transactions) =>
            Object.assign([...(transactions || []), transaction.id], {
              partial: !transactions || transactions.partial,
            })
          );
        });
      },
      statementsFor(
        creditLineId: CreditLine.Id,
        statements: CreditLine.Statement[]
      ) {
        setStore(
          "statementsByCreditLineId",
          creditLineId,
          reconcile(statements, { key: "id" })
        );
      },
      predictedPaymentsFor(
        creditLineId: CreditLine.Id,
        predictedPayments: FuturePayment[]
      ) {
        setStore(
          "predictedPaymentsByCreditLineId",
          creditLineId,
          reconcile(predictedPayments, {
            // TODO: wait for solid to support derived keys
            key: "dueAt",
          })
        );
      },
    };

    const us = {
      mutations: {},
      actions: {
        // TODO: US Credit line features
        close: UFunction.todo,
        open: UFunction.todo,
        fetch(_id?: CreditLine.Id) {
          return usRpc.Account.getUserInfo({
            scopes: [User.Scope.CREDIT_LINE],
          }).then(({ user }) => {
            const creditLines = [
              user!.openCreditLine,
              ...user!.closedCreditLines!,
            ].filter(UBoolean.isTruthy);

            batch(() => {
              mutations.all(creditLines.map(CreditLineUsa.normalize));
              for (const creditLine of creditLines) {
                mutations.statementsFor(
                  creditLine.token,
                  creditLine.statements.map(CreditLineUsa.Statement.normalize)
                );
                mutations.transactionsFor(
                  creditLine.token,
                  creditLine.transactions.map(TransactionUsa.normalize)
                );
                mutations.predictedPaymentsFor(
                  creditLine.token,
                  creditLine.paymentSchedule!.payments.map(
                    FuturePaymentUsa.normalize
                  )
                );
              }
            });
          });
        },
        fetchExpectedDueDate(): Promise<void> {
          const anyPlan = subscriptions.defaultPlans()[0];
          assert(anyPlan, "Subscription plans must be loaded.");

          const productToken = anyPlan.metadata?.productToken;
          assert(
            productToken,
            "Subscription plan must be loaded with a product token, see SubscriptionUsa.Plan.normalize."
          );

          return usRpc.Shopping.checkoutPreview({
            productToken,
          })
            .SUCCESS(({ paymentSchedule }) => {
              setStore(
                "expectedDueDate",
                Protobuf.Timestamp.toDate(
                  paymentSchedule?.payments[0]?.dueDate!
                )
              );
            })
            .elseThrow(t`Failed to fetch expected due date.`);
        },
        fetchPredictedPayments: (id: CreditLine.Id) => us.actions.fetch(id),
        fetchStatements: (id: CreditLine.Id) => us.actions.fetch(id),
        async fetchTransactions(id: CreditLine.Id) {
          await us.actions.fetch(id);

          return accessors.transactionsFor(id);
        },
        makePayment: Chainable.deferrable(
          ({ defer = false }) =>
            (
              creditLineId: CreditLine.Id,
              paymentMethodId: PaymentMethod.Id
            ) => {
              const amountCents =
                accessors.byId(creditLineId)!.outstandingMinimumPaymentCents;

              track.creditAccount.payment("attempt", {
                amountCents,
              });

              return usRpc.CreditLine.makeCreditLinePayment({
                creditLineToken: creditLineId,
                paymentMethodToken: paymentMethodId,
                amountCents,
              })
                .SUCCESS(({ creditLine }) => {
                  const transactions = creditLine!.transactions.map(
                    TransactionUsa.normalize
                  );

                  const transaction = UArray.maxBy(
                    transactions,
                    ({ operation, transactedAt }) =>
                      operation === "payment" ? transactedAt : undefined
                  )!;

                  if (
                    transactions.filter((t) => t.operation === "payment")
                      .length === 1
                  ) {
                    analytics.convert("Make Credit Line Payment", {
                      tier: subscriptions.active()?.plan.tier || "basic",
                      accountId: creditLineId,
                      transactionId: transaction!.id,
                    });
                  }

                  track.creditAccount.payment("success", {
                    amountCents,
                  });

                  if (defer) return resolve;

                  const res = resolve();
                  return () => res;

                  function resolve() {
                    mutations.transactionsFor(creditLineId, transactions);
                    mutations.one(CreditLineUsa.normalize(creditLine!));
                    return transaction;
                  }
                })
                .elseThrow(t`Failed to make credit line payment.`)
                .catch((e) => {
                  track.creditAccount.payment("failure", {
                    errors: e instanceof Error ? [e.message] : [],
                    amountCents,
                  });
                  throw e;
                });
            }
        ),
        updateAutopay: Chainable.create(
          Chainable.wrapFactory(
            ({ defer = false }) =>
              (
                creditLineId: CreditLine.Id,
                autopay: Partial<Recurrence.Settings>
              ) =>
                usRpc.CreditLine.updateCreditLineSettings({
                  creditLineToken: creditLineId,
                  autoPay: RecurrenceUsa.Settings.denormalize(autopay),
                })
                  .SUCCESS(({ creditLine }) => {
                    if (!defer) resolve();
                    return resolve;

                    function resolve() {
                      mutations.one(CreditLineUsa.normalize(creditLine!));
                    }
                  })
                  .elseThrow(t`Failed to update autopay.`)
          ),
          { defer: { defer: true } }
        ),
        schedulePayment: Chainable.create(
          Chainable.wrapFactory(
            ({ defer = false }) =>
              (
                creditLineId: CreditLine.Id,
                {
                  paymentMethodId,
                  scheduledAt,
                }: {
                  paymentMethodId: PaymentMethod.Id;
                  scheduledAt: Date;
                }
              ) =>
                usRpc.CreditLine.createFirstPaymentDate({
                  creditLineToken: creditLineId,
                  paymentMethodToken: paymentMethodId,
                  scheduledAt: Protobuf.Timestamp.fromDate(scheduledAt),
                }).then(({ creditLine }) => {
                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    mutations.one(CreditLineUsa.normalize(creditLine!));
                  }
                })
          ),
          { defer: { defer: true } }
        ),
      },
    };

    const canada = {
      actions: {
        open(agreementId: CreditLine.Agreement.Id) {
          track.creditAccount.open("attempt", { agreementId });

          return canadaRpc.CreditLine.openCreditLine({
            agreementId,
          })
            .then(({ creditLine }) => {
              analytics.convert("Credit Account Open", {
                tier:
                  // Match subscription to agreement, subscription activation
                  // occurs after opening credit account
                  subscriptions
                    .defaultPlans()
                    .find(
                      (plan) => plan.creditLineAgreement?.id === agreementId
                    )?.tier || "basic",
                accountId: creditLine!.id,
              });
              track.creditAccount.open("success", { agreementId });

              mutations.one(CreditLineCanada.normalize(creditLine!));
              return creditLine!.id as CreditLine.Id;
            })
            .catch((e) => {
              track.creditAccount.open("failure", {
                errors: e instanceof Error ? [e.message] : [],
                agreementId,
              });
              throw e;
            });
        },
        fetch(_id?: CreditLine.Id) {
          return canadaRpc.CreditLine.listCreditLines({}).then(
            ({ creditLines }) => {
              mutations.all(creditLines.map(CreditLineCanada.normalize));
            }
          );
        },
        fetchStatements(creditLineId: CreditLine.Id) {
          if (!creditLineId) return Promise.resolve();
          return canadaRpc.CreditLine.listStatements({ creditLineId }).then(
            ({ statements }) => {
              mutations.statementsFor(
                creditLineId,
                statements.map(CreditLineCanada.Statement.normalize)
              );
            }
          );
        },
        fetchTransactions(creditLineId: CreditLine.Id) {
          return canadaRpc.CreditLine.listTransactions({
            creditLineId,
          }).then(({ transactions }) => {
            const normalizedTransactions = transactions.map(
              TransactionCanada.normalize
            );

            mutations.transactionsFor(creditLineId, normalizedTransactions);
            return normalizedTransactions;
          });
        },
        fetchPredictedPayments(creditLineId: CreditLine.Id) {
          return canadaRpc.CreditLine.listPredictedPayments({
            creditLineId,
          }).then(({ payments }) => {
            setStore(
              "predictedPaymentsByCreditLineId",
              creditLineId,
              payments.map(FuturePaymentCanada.normalize)
            );
          });
        },
        fetchExpectedDueDate() {
          return canadaRpc.CreditLine.previewStatementDueDate({
            statementDate: Protobuf.Timestamp.fromDate(new Date()),
          }).then(({ statementDueDate }) => {
            setStore(
              "expectedDueDate",
              Protobuf.Timestamp.toDate(statementDueDate!)
            );
          });
        },
        // TODO: Add amountCents
        makePayment: Chainable.deferrable(
          ({ defer = false }) =>
            (
              creditLineId: CreditLine.Id,
              paymentMethodId: PaymentMethod.Id
            ) => {
              const amountCents =
                accessors.byId(creditLineId)!.outstandingMinimumPaymentCents;

              track.creditAccount.payment("attempt", {
                amountCents,
              });

              return canadaRpc.CreditLine.makePayment({
                creditLineId,
                paymentMethodId,
              })
                .then(({ transaction, creditLine }) => {
                  actions
                    .fetchTransactions(creditLineId)
                    .then((transactions) => {
                      if (
                        transactions.filter(
                          ({ operation }) => operation === "payment"
                        ).length === 1
                      ) {
                        analytics.convert("Make Credit Line Payment", {
                          tier: subscriptions.active()?.plan.tier || "basic",
                          accountId: creditLineId,
                          transactionId: transaction!.id,
                        });
                      }
                    });

                  track.creditAccount.payment("success", {
                    amountCents,
                  });

                  if (defer) return resolve;

                  const res = resolve();
                  return () => res;

                  function resolve() {
                    const t = TransactionCanada.normalize(transaction!);

                    mutations.addTransactionFor(creditLineId, t);
                    mutations.one(CreditLineCanada.normalize(creditLine!));
                    return t;
                  }
                })
                .catch((e) => {
                  track.creditAccount.payment("failure", {
                    errors: e instanceof Error ? [e.message] : [],
                    amountCents,
                  });
                  throw e;
                });
            }
        ),
        updateAutopay: Chainable.create(
          Chainable.wrapFactory(
            ({ defer = false }) =>
              (
                creditLineId: CreditLine.Id,
                autopay: Partial<Recurrence.Settings>
              ) =>
                canadaRpc.CreditLine.updateSettings({
                  creditLineId,
                  autopay: RecurrenceCanada.Settings.denormalize(autopay),
                }).then(({ creditLine }) => {
                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    mutations.one(CreditLineCanada.normalize(creditLine!));
                  }
                })
          ),
          { defer: { defer: true } }
        ),
        schedulePayment: Chainable.create(
          Chainable.wrapFactory(
            ({ defer = false }) =>
              (
                creditLineId: CreditLine.Id,
                {
                  paymentMethodId,
                  scheduledAt,
                }: {
                  paymentMethodId: PaymentMethod.Id;
                  scheduledAt: Date;
                }
              ) =>
                canadaRpc.CreditLine.schedulePayment({
                  creditLineId,
                  paymentMethodId,
                  scheduleAt: Protobuf.Timestamp.fromDate(scheduledAt),
                }).then(({ creditLine }) => {
                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    mutations.one(CreditLineCanada.normalize(creditLine!));
                  }
                })
          ),
          { defer: { defer: true } }
        ),
        close(
          creditLineId: CreditLine.Agreement.Id,
          closureReason: string,
          closureNote: string
        ) {
          track.creditAccount.close("attempt");
          return canadaRpc.CreditLine.closeCreditLine({
            creditLineId,
            closureReason,
            closureNote,
          })
            .then(async ({ creditLine }) => {
              track.creditAccount.close("success");
              await setSubscriptions.fetch();
              mutations.one(CreditLineCanada.normalize(creditLine!));
            })
            .catch((e) => {
              track.creditAccount.close("failure", {
                errors: e instanceof Error ? [e.message] : [],
              });
              throw e;
            });
        },
      },
    };

    const actions = withLogger("action", {
      one: mutations.one,
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
    });

    return { accessors, actions };
  }
);
